// src/components/pages/RegisterBusiness.jsx
import React, { useState } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  FiArrowRight, FiArrowLeft, FiLayers, FiMail, FiLock, FiUser,
  FiPhone, FiGlobe, FiFileText, FiBriefcase, FiUsers, FiEye, FiEyeOff
} from 'react-icons/fi';

const COUNTRIES = [
  'Peru', 'Mexico', 'Colombia', 'Argentina', 'Chile', 'Ecuador',
  'Bolivia', 'Venezuela', 'Uruguay', 'Paraguay', 'Brasil',
  'Costa Rica', 'Panama', 'Espana', 'Estados Unidos', 'Otro'
];

const INDUSTRIES = [
  'Educacion', 'Tecnologia', 'Salud', 'Finanzas', 'Retail',
  'Manufactura', 'Servicios', 'Consultoria', 'Marketing',
  'Alimentos', 'Transporte', 'Construccion', 'Otro'
];

const SIZES = [
  { value: '1-10', label: '1-10 empleados' },
  { value: '11-50', label: '11-50 empleados' },
  { value: '51-200', label: '51-200 empleados' },
  { value: '201-500', label: '201-500 empleados' },
  { value: '500+', label: 'Mas de 500' },
];

const RegisterBusiness = () => {
  const { registerWithEmail, registerWithGoogle, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: datos personales, 2: datos empresa
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1 - Cuenta
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Step 2 - Empresa
    companyName: '',
    ruc: '',
    phone: '',
    country: 'Peru',
    industry: 'Educacion',
    size: '1-10',
  });
  const [errors, setErrors] = useState({});

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!formData.displayName.trim()) e.displayName = 'Nombre requerido';
    if (!formData.email.trim()) e.email = 'Email requerido';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Email invalido';
    if (!formData.password) e.password = 'Contrasena requerida';
    else if (formData.password.length < 6) e.password = 'Minimo 6 caracteres';
    if (formData.password !== formData.confirmPassword) e.confirmPassword = 'Las contrasenas no coinciden';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!formData.companyName.trim()) e.companyName = 'Nombre de empresa requerido';
    if (!formData.phone.trim()) e.phone = 'Telefono requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    try {
      await registerWithEmail(formData.email, formData.password, formData.displayName, {
        name: formData.companyName,
        ruc: formData.ruc,
        phone: formData.phone,
        country: formData.country,
        industry: formData.industry,
        size: formData.size,
      });
      // El AuthContext redirigira a PendingApproval automaticamente
    } catch {
      // El error ya se maneja en AuthContext con toast
    }
  };

  const handleGoogleRegister = async () => {
    if (step === 1) {
      // Para Google, necesitamos datos de empresa primero
      setStep(2);
      return;
    }
    if (!validateStep2()) return;
    try {
      await registerWithGoogle({
        name: formData.companyName,
        ruc: formData.ruc,
        phone: formData.phone,
        country: formData.country,
        industry: formData.industry,
        size: formData.size,
      });
    } catch {
      // Error manejado en AuthContext
    }
  };

  const InputField = ({ icon: Icon, label, field, type = 'text', placeholder, required = true }) => (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label} {required && <span className="text-red-400">*</span>}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
        <input
          type={type === 'password' && showPassword ? 'text' : type}
          value={formData[field]}
          onChange={e => updateField(field, e.target.value)}
          placeholder={placeholder}
          className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all ${errors[field] ? 'border-red-500/50' : 'border-white/10'}`}
        />
        {type === 'password' && (
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
            {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
          </button>
        )}
      </div>
      {errors[field] && <p className="text-xs text-red-400 mt-1">{errors[field]}</p>}
    </div>
  );

  const SelectField = ({ icon: Icon, label, field, options }) => (
    <div>
      <label className="block text-xs font-medium text-gray-400 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
        <select
          value={formData[field]}
          onChange={e => updateField(field, e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all appearance-none cursor-pointer"
        >
          {options.map(opt => (
            <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value} className="bg-gray-900">
              {typeof opt === 'string' ? opt : opt.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex">
      {/* Left panel - Branding */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-purple-600/10 to-transparent" />
        <div className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-indigo-600/15 rounded-full blur-[120px]" />
        <div className="relative z-10 max-w-md px-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <FiLayers size={22} />
            </div>
            <span className="text-2xl font-bold">Hingenia Suite</span>
          </div>
          <h2 className="text-3xl font-bold mb-4 leading-tight">
            Lleva tu empresa al{' '}
            <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">siguiente nivel</span>
          </h2>
          <p className="text-gray-400 leading-relaxed mb-8">
            Gestiona tareas, cursos, asistencia, ventas y mas en una sola plataforma.
            Potenciado con IA para equipos que quieren crecer.
          </p>
          <div className="space-y-4">
            {[
              'Modulos personalizables por empresa',
              'Colaboracion en tiempo real',
              'Reportes y metricas avanzadas',
              'Soporte dedicado'
            ].map((feat, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-gray-300">
                <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                {feat}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <FiLayers size={18} />
            </div>
            <span className="text-lg font-bold">Hingenia Suite</span>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-3 mb-8">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${step === 1 ? 'bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/30' : 'bg-white/5 text-gray-500'}`}>
              <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-[10px]">1</span>
              Tu cuenta
            </div>
            <div className="flex-1 h-px bg-white/10" />
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${step === 2 ? 'bg-indigo-500/20 text-indigo-400 ring-1 ring-indigo-500/30' : 'bg-white/5 text-gray-500'}`}>
              <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-[10px]">2</span>
              Tu empresa
            </div>
          </div>

          <h1 className="text-2xl font-bold mb-1">
            {step === 1 ? 'Crea tu cuenta' : 'Datos de tu empresa'}
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            {step === 1
              ? 'Ingresa tus datos personales para comenzar'
              : 'Cuentanos sobre tu empresa para personalizar tu experiencia'
            }
          </p>

          <form onSubmit={step === 1 ? (e) => { e.preventDefault(); handleNext(); } : handleSubmit}>
            {step === 1 ? (
              <div className="space-y-4">
                <InputField icon={FiUser} label="Nombre completo" field="displayName" placeholder="Juan Perez" />
                <InputField icon={FiMail} label="Email corporativo" field="email" type="email" placeholder="juan@empresa.com" />
                <InputField icon={FiLock} label="Contrasena" field="password" type="password" placeholder="Minimo 6 caracteres" />
                <InputField icon={FiLock} label="Confirmar contrasena" field="confirmPassword" type="password" placeholder="Repite tu contrasena" />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-sm font-bold shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  Siguiente <FiArrowRight size={16} />
                </button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                  <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#0a0a0f] text-gray-600">o registrate con</span></div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleRegister}
                  disabled={loading}
                  className="w-full py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <InputField icon={FiBriefcase} label="Nombre de empresa" field="companyName" placeholder="Mi Empresa S.A.C." />
                <InputField icon={FiFileText} label="RUC / NIT / RFC" field="ruc" placeholder="20123456789" required={false} />
                <InputField icon={FiPhone} label="Telefono" field="phone" placeholder="+51 999 999 999" />
                <SelectField icon={FiGlobe} label="Pais" field="country" options={COUNTRIES} />
                <SelectField icon={FiBriefcase} label="Industria" field="industry" options={INDUSTRIES} />
                <SelectField icon={FiUsers} label="Tamano de empresa" field="size" options={SIZES} />

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                  >
                    <FiArrowLeft size={16} /> Atras
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-[2] py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl text-sm font-bold shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? 'Registrando...' : 'Registrar empresa'}
                    {!loading && <FiArrowRight size={16} />}
                  </button>
                </div>

                <div className="relative my-2">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                  <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#0a0a0f] text-gray-600">o</span></div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleRegister}
                  disabled={loading}
                  className="w-full py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm font-medium hover:bg-white/10 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Registrar con Google
                </button>
              </div>
            )}
          </form>

          <p className="text-center text-sm text-gray-600 mt-6">
            Ya tienes cuenta?{' '}
            <Link to="/login" className="text-indigo-400 hover:text-indigo-300 font-medium">
              Iniciar sesion
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterBusiness;
